/**
 * Utilidade para análise e normalização de mídias (imagens/vídeos)
 * Padrão implementado conforme commit b6c108f
 */

export interface ImagemNormalizada {
  id: number
  imagem: string
  imagemPreview: string
  posicaoImagem?: string
  tipo: 'image' | 'video'
}

/**
 * Lista de extensões de vídeo suportadas
 */
export const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.ogg']

/**
 * Detecta se uma URL é um vídeo pela extensão do arquivo
 * @param url URL ou caminho do arquivo
 * @returns true se for vídeo, false caso contrário
 */
export const isVideo = (url: string): boolean => {
  if (!url || typeof url !== 'string') return false

  const cleanUrl = url.toLowerCase().split('?')[0] // Remove query strings

  return VIDEO_EXTENSIONS.some((ext) => cleanUrl.endsWith(ext))
}

/**
 * Normaliza dados de mídia vindos do backend
 * Converte diferentes formatos para o padrão esperado pelo frontend
 * @param imagemData Dados brutos de mídia do servidor
 * @param index Índice da mídia (para gerar ID único se necessário)
 * @returns Mídia normalizada com tipo detectado
 */
export const normalizarMidia = (
  imagemData: any,
  index = 0
): ImagemNormalizada => {
  // Extrai URL da mídia (pode vir em diferentes campos)
  const url = (
    imagemData?.url ||
    imagemData?.imagem ||
    imagemData?.dscUriCdn ||
    imagemData?.dscMidia ||
    ''
  ).toString()

  // Decodifica URL se necessário
  const urlDecodificada = decodeURIComponent(url)

  // Detecta tipo de mídia
  const tipo = isVideo(urlDecodificada) ? 'video' : 'image'

  return {
    id: imagemData?.id ?? imagemData?.seqMidia ?? Date.now() + index,
    imagem: urlDecodificada,
    imagemPreview: urlDecodificada,
    posicaoImagem:
      imagemData?.posicaoImagem?.toString() ||
      imagemData?.codPropriedade?.toString() ||
      '',
    tipo
  }
}

/**
 * Normaliza um array de mídias, tratando diferentes formatos vindos do backend
 * @param meio Pode ser: array, objeto, string, undefined, null
 * @returns Array de mídias normalizadas
 */
export const normalizarArrayMidias = (meio: any): ImagemNormalizada[] => {
  if (!meio) return []

  // Se é um array, mapeia cada item
  if (Array.isArray(meio)) {
    return meio.map((item, idx) => normalizarMidia(item, idx))
  }

  // Se é um objeto único, converte para array com um item
  if (typeof meio === 'object') {
    return [normalizarMidia(meio, 0)]
  }

  // Se é string (URL direto)
  if (typeof meio === 'string') {
    return [normalizarMidia({ url: meio }, 0)]
  }

  return []
}

/**
 * Valida se uma mídia tem os campos obrigatórios preenchidos
 * @param midia Mídia a validar
 * @returns true se válida, false caso contrário
 */
export const validarMidia = (midia: ImagemNormalizada): boolean => {
  return !!(midia?.imagem && midia?.tipo)
}

/**
 * Filtra mídias válidas de um array
 * @param midias Array de mídias
 * @returns Array contendo apenas mídias válidas
 */
export const filtrarMidiasValidas = (
  midias: ImagemNormalizada[]
): ImagemNormalizada[] => {
  return midias.filter(validarMidia)
}
